import React, { useState, useContext } from "react";
import { useLocalization } from "@fluent/react";
import { Button, formatNumber } from "@narmi/design_system";
import { useFeature } from "byzantine";
import cc from "classcat";
import PropTypes from "prop-types";
import ReactMarkdown from "react-markdown";
import { Dialog } from "cerulean";
import InstitutionSettingsContext from "../contexts/InstitutionSettingsContext";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useLimits } from "../contexts/LimitsContext";

const hasLimitType = (limits, transactionType) =>
  // this determines whether a segment or sub-user limit exists for the transaction type
  !!(limits[transactionType] || limits[`organization_user_${transactionType}`]);

export const RenderLimit = ({
  limits,
  transactionType,
  useParagraphTag = true,
}) => {
  const { l10n } = useLocalization();
  if (!limits[transactionType]) return null;
  const periods = limits[transactionType];
  const productScopedLimits = Object.keys(limits).filter((limitType) => limitType.startsWith(transactionType) && limitType !== transactionType);

  const baseLimitDescriptions = Object.entries(periods).map(([period, limit]) => {
    if (
      Number.isNaN(parseInt(limit?.amount, 10)) ||
      Number.isNaN(parseInt(limit?.amount_available, 10))
    ) {
      return null;
    }

    // only display "<amount_available> left" if amount_available differs from amount
    const limitsFluentVars = {
      period: Number(period),
      amount: formatNumber(limit.amount),
      amountLeft: formatNumber(limit.amount_available),
    };
    const content =
      limit.amount !== limit.amount_available
        ? l10n.getString("transfer-limit-amount-left", limitsFluentVars)
        : l10n.getString("transfer-limit-amount", limitsFluentVars);

    return useParagraphTag ? (
      <p className="margin--none" key={`${transactionType}-${period}-p`}>
        {content}
      </p>
    ) : (
      content
    );
  });

  productScopedLimits.forEach(productLimit => {
    const productLimitDescriptions = Object.entries(limits[productLimit]).map(([period, limit]) => {
      if (
        Number.isNaN(parseInt(limit?.amount, 10)) ||
        Number.isNaN(parseInt(limit?.amount_available, 10))
      ) {
        return null;
      }

      const limitSplit = productLimit.split(".");
      const product = limitSplit.length > 1 ? limitSplit[1].replaceAll("_", " ") : null;
      const limitsFluentVars = {
        period: Number(period),
        amount: formatNumber(limit.amount),
        amountLeft: formatNumber(limit.amount_available),
        product
      };
      const content =
        limit.amount !== limit.amount_available
          ? l10n.getString("product-transfer-limit-amount-left", limitsFluentVars)
          : l10n.getString("product-transfer-limit-amount", limitsFluentVars);

      return useParagraphTag ? (
        <p className="margin--none" key={`${productLimit}-${period}-p`}>
          {content}
        </p>
      ) : (
        content
      );
    });
    baseLimitDescriptions.push(...productLimitDescriptions);
  });

  return baseLimitDescriptions;
};
RenderLimit.propTypes = {
  limits: PropTypes.object,
  transactionType: PropTypes.string,
  useParagraphTag: PropTypes.bool,
};

const LimitsDialog = ({ type = ["ach", "fednow_send"], limitsForDisplay }) => {
  const { l10n } = useLocalization();
  const { currentUser } = useCurrentUser();
  const isBusiness = currentUser?.isBusiness();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { featureEnabled } = useFeature();
  const { transfer_disclosure, short_name, wire_info, wire_disclosure, international_wire_disclosure, instant_transfer_disclosure } =
    useContext(InstitutionSettingsContext);
  // TODO: always use limits from the context
  const contextLimits = useLimits();
  const limits = limitsForDisplay || contextLimits;
  const showLimitsSection = Object.keys(limits).length > 0;
  const showDisclosureSection = (
    (type.includes("domestic_wire") && (wire_info || wire_disclosure)) ||
    (type.includes("international_wire") && international_wire_disclosure) ||
    (type.includes("ach") && transfer_disclosure) ||
    (type.includes("fednow_send") && instant_transfer_disclosure)
  );
  const showInstant = featureEnabled("fednow_send");

  return (
    <>
      {(showLimitsSection || showDisclosureSection) && (
        <Button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          label={
            (type.includes("domestic_wire") || type.includes("international_wire"))
              ? l10n.getString("view-wire-transfer-limits")
              : l10n.getString("view-transfer-limits")
          }
          testId="view-limits-and-disclosures-button"
          kind="plain"
          size="s"
        />
      )}
      <Dialog
        isOpen={isDialogOpen}
        onUserDismiss={() => setIsDialogOpen(false)}
        title={l10n.getString("title-limits-and-disclosures")}
      >
        <div>
          <div className="fontColor--secondary">
            {type.includes("ach") && hasLimitType(limits, "ach_push") && (
              <>
                <div
                  className="margin--top--s fontWeight--semibold fontColor--heading"
                  style={{ marginBottom: "6px" }}
                >
                  {l10n.getString("limit-external-push-title")}
                </div>
                <p>
                  {l10n.getString("limit-external-push-details", {
                    accountName: short_name,
                  })}
                </p>
                {isBusiness && limits?.organization_user_ach_push && (
                  <>
                    <div className="fontColor--heading">
                      {l10n.getString("limit-personal-title")}
                    </div>
                    <RenderLimit
                      limits={limits}
                      transactionType="organization_user_ach_push"
                    />
                    <br />
                  </>
                )}
                {isBusiness && limits?.ach_push && (
                  <div className="fontColor--heading">
                    {l10n.getString("limit-organizational-title")}
                  </div>
                )}
                {limits?.ach_push && (
                  <>
                    <RenderLimit
                      limits={limits}
                      transactionType="ach_push"
                    />
                  </>
                )}
              </>
            )}
            {type.includes("ach") && limits?.ach_pull && (
              <>
                <div
                  className={cc([
                    "fontWeight--semibold fontColor--heading",
                    {
                      "margin--top--xl": hasLimitType(
                        limits,
                        "ach_push",
                      ),
                      "margin--top--s": !hasLimitType(
                        limits,
                        "ach_push",
                      ),
                    },
                  ])}
                  style={{ marginBottom: "6px" }}
                >
                  {l10n.getString("limit-external-pull-title")}
                </div>
                <p>
                  {l10n.getString("limit-external-pull-details", {
                    accountName: short_name,
                  })}
                </p>
                {isBusiness && (
                  <div className="fontColor--heading">
                    {l10n.getString("limit-organizational-title")}
                  </div>
                )}
                <RenderLimit
                  limits={limits}
                  transactionType="ach_pull"
                />
                <br />
              </>
            )}
            {type.includes("ach") && limits?.immediate_ach_pull && (
              <p>
                {l10n.getString("funds-available-from-external-pull", {
                  accountName: short_name,
                  amount: RenderLimit({
                    limits,
                    transactionType: "immediate_ach_pull",
                    useParagraphTag: false,
                  })[0],
                })}
              </p>
            )}
            {type.includes("domestic_wire") && hasLimitType(limits, "wire") && (
              <>
                <div
                  className="margin--top--s fontWeight--semibold fontColor--heading"
                  style={{ marginBottom: "6px" }}
                >
                  {l10n.getString("limit-external-transfer-title")}
                </div>
                <p>
                  {l10n.getString("limit-external-transfer-details", {
                    accountName: short_name,
                  })}
                </p>
                {isBusiness && limits?.organization_user_wire && (
                  <>
                    <div className="fontColor--heading">
                      {l10n.getString("limit-personal-title")}
                    </div>
                    <RenderLimit
                      limits={limits}
                      transactionType="organization_user_wire"
                    />
                    <br />
                  </>
                )}
                {isBusiness && limits?.wire && (
                  <div className="fontColor--heading">
                    {l10n.getString("limit-organizational-title")}
                  </div>
                )}
                {limits?.wire && (
                  <RenderLimit
                    limits={limits}
                    transactionType="wire"
                  />
                )}
              </>
            )}
            {type.includes("international_wire") && hasLimitType(limits, "international_wire") && (
              <>
                <div
                  className={cc([
                    "fontWeight--semibold fontColor--heading",
                    {
                      "margin--top--xl": hasLimitType(
                        limits,
                        "wire",
                      ),
                      "margin--top--s": !hasLimitType(
                        limits,
                        "wire",
                      ),
                    },
                  ])}
                  style={{ marginBottom: "6px" }}
                >
                  {l10n.getString("limit-international-transfer-title")}
                </div>
                <p>
                  {l10n.getString("limit-international-transfer-details", {
                    accountName: short_name,
                  })}
                </p>
                {isBusiness && limits?.organization_user_international_wire && (
                  <>
                    <div className="fontColor--heading">
                      {l10n.getString("limit-personal-title")}
                    </div>
                    <RenderLimit
                      limits={limits}
                      transactionType="organization_user_international_wire"
                    />
                    <br />
                  </>
                )}
                {isBusiness && limits?.international_wire && (
                  <div className="fontColor--heading">
                    {l10n.getString("limit-organizational-title")}
                  </div>
                )}
                {limits?.international_wire && (
                  <RenderLimit
                    limits={limits}
                    transactionType="international_wire"
                  />
                )}
              </>
            )}
            {type.includes("fednow_send") && showInstant && hasLimitType(limits, "fednow_send") && (
              <>
                <div
                  className="margin--top--s fontWeight--semibold fontColor--heading"
                  style={{ marginBottom: "6px" }}
                >
                  {l10n.getString("limit-fednow-send-title")}
                </div>
                <p>
                  {l10n.getString("limit-fednow-send-details", {
                    accountName: short_name,
                  })}
                </p>
                {limits?.fednow_send && (
                  <RenderLimit
                    limits={limits}
                    transactionType="fednow_send"
                  />
                )}
              </>
            )}
          </div>
          {showDisclosureSection && (
            <div
              className={cc([
                "fontColor--heading fontWeight--semibold",
                {
                  "margin--top--xl": showLimitsSection,
                  "margin--top--s": !showLimitsSection,
                },
              ])}
              style={{ marginBottom: "6px" }}
            >
              {l10n.getString("limit-disclosures-title")}
            </div>
          )}
          {type.includes("ach") && (
            <div className="fontColor--secondary">
              <ReactMarkdown>
                {transfer_disclosure}
              </ReactMarkdown>
            </div>
          )}
          {type.includes("domestic_wire") && (
            <>
              <div
                className="fontColor--secondary"
                style={{
                  marginBottom: "6px",
                }}
              >
                <ReactMarkdown>{wire_info}</ReactMarkdown>
                <ReactMarkdown>
                  {wire_disclosure}
                </ReactMarkdown>
              </div>
            </>
          )}
          {type.includes("international_wire") && (
            <div className="fontColor--secondary">
              <ReactMarkdown>
                {international_wire_disclosure}
              </ReactMarkdown>
            </div>
          )}
          {showInstant && type.includes("fednow_send") && (
            <div className="fontColor--secondary padding--y">
              <ReactMarkdown>
                {instant_transfer_disclosure}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

LimitsDialog.propTypes = {
  limitsForDisplay: PropTypes.object,
  type: PropTypes.arrayOf(PropTypes.string),
};

export default LimitsDialog;
